import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { ChipOwnProps, SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip, { chipClasses } from '@mui/material/Chip';
import { ProjectState, ProjectStateEvent } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Project } from '~hooks/useProjects/models';

import ProjectStatusDropdown from './ProjectStatusDropdown';

const PROPS_BY_PROJECT_STATE: Record<
  ProjectState,
  Pick<ChipOwnProps, 'color' | 'label'>
> = {
  [ProjectState.DRAFT]: { color: 'warning', label: t('status.draft') },
  [ProjectState.QUOTED]: { color: 'secondary', label: t('status.quoted') },
  [ProjectState.LOST]: { color: 'error', label: t('status.lost') },
  [ProjectState.ACTIVE]: { color: 'info', label: t('status.active') },
  [ProjectState.COMPLETED]: { color: 'success', label: t('status.completed') },
  [ProjectState.INACTIVE]: { color: 'default', label: t('status.inactive') },
  [ProjectState.CANCELED]: { color: 'error', label: t('status.canceled') },
};

interface ProjectStatusChipProps {
  onSelectEvent?: (event: ProjectStateEvent) => void;
  pendingEvent?: ProjectStateEvent | null;
  project: Project;
  sx?: SxProps<Theme>;
}

export default function ProjectStatusChip({
  onSelectEvent,
  pendingEvent,
  project,
  sx,
}: ProjectStatusChipProps) {
  if (!project.state) return null;

  const { color, label } = PROPS_BY_PROJECT_STATE[project.state];
  const isInteractive = Boolean(onSelectEvent) && project.stateEvents.length > 0;

  const chipLabel = isInteractive ? (
    <Box sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.25 }}>
      {label}
      <KeyboardArrowDown sx={{ fontSize: 14, mr: -0.5 }} />
    </Box>
  ) : (
    label
  );

  const chip = (
    <Chip
      color={color}
      clickable={isInteractive}
      data-test-id={`project-status-chip-${project.id}`}
      icon={undefined}
      label={chipLabel}
      size="small"
      variant="outlined"
      sx={[
        {
          [`&.${chipClasses.outlined}`]: { borderRadius: 1 },
          [`.${chipClasses.label}`]: { fontSize: '12px', fontWeight: 700 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );

  if (!isInteractive || !onSelectEvent) {
    return chip;
  }

  return (
    <ProjectStatusDropdown
      events={project.stateEvents}
      onSelectEvent={onSelectEvent}
      pendingEvent={pendingEvent}
      trigger={chip}
    />
  );
}
